import React, { useEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import {
  arrayOf,
  bool,
  func,
  number,
  shape,
  string
} from 'prop-types';
import { useScript } from '../hooks/useScript';
import { storeShape } from './storeShape';
import { StoreOverlay } from './StoreOverlay/StoreOverlay';
import { StorePin } from './StorePin/StorePin';

/* global Microsoft */

const StoreMap = ({
  mapOptions,
  omitStoreNumber,
  onMapViewChange,
  onStoreClick,
  stores,
  storeMapId
}) => {
  const [bingMap, setBingMap] = useState({});
  const [infoBox, setInfoBox] = useState({});
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const DEFAULT_LAT = 33.862076;
  const DEFAULT_LNG = -84.476212;

  let mapOptionsMerged = { ...StoreMap.defaultProps.mapOptions, ...mapOptions };

  const handlePinClick = ({ event, store }) => {
    onStoreClick(store);
    const location = event?.target?.getLocation();
    const { latitude, longitude } = location || {};

    if (!mapOptionsMerged?.disableInfoBox) {
      const infoBoxOptions = {
        htmlContent: renderToString(<StoreOverlay store={store} />),
        offset: new Microsoft.Maps.Point(-26, 45),
        location,
        showCloseButton: true,
        visible: true
      };
      infoBox.setOptions(infoBoxOptions);

      const latOffset = 0.25;

      if (!mapOptionsMerged?.disablePanning) {
        bingMap.setView({
          center: new Microsoft.Maps.Location(latitude + latOffset, longitude + 0.15), zoom: 10
        });
      }
    }
  };

  const generatePins = () => {
    const pins = [];

    if (stores?.length > 0 && stores[0] !== undefined) {
      stores.forEach((store, index) => {
        const isFirstPin = index === 0;
        const storeNumberToOmit = omitStoreNumber.length < 4 ? '0' + omitStoreNumber : omitStoreNumber;
        const isExcludedStore = isFirstPin && (store?.storeId === storeNumberToOmit);
        const storePinIndex = (omitStoreNumber === '') ? index + 1 : index;
        const hideIndex = isExcludedStore || stores?.length < 2;

        const pinOptions = {
          icon: renderToString(
            <StorePin
              color={isFirstPin ? '#00AE4D' : '#F96302'}
              index={hideIndex ? '' : storePinIndex}
            />
          ),
          text: '',
          width: 30,
          height: 40,
          id: 'pin' + store?.storeId
        };

        const { lat, lng } = store?.coordinates || {};
        const loc = new Microsoft.Maps.Location(lat, lng);
        const pin = new Microsoft.Maps.Pushpin(loc, pinOptions);

        Microsoft.Maps.Events.addHandler(pin, 'click', function (event) {
          handlePinClick({ event, store });
        });

        pins.push(pin);
      });
    }

    return pins;
  };

  const clearPins = () => {
    if (bingMap && bingMap?.entities) {
      for (let i = bingMap.entities.getLength() - 1; i >= 0; i -= 1) {
        const pushpin = bingMap.entities.get(i);
        if (pushpin instanceof Microsoft.Maps.Pushpin) {
          bingMap.entities.removeAt(i);
        }
      }
    }
  };

  const populatePinsOnMap = ({ mapTemp }) => {
    if (mapTemp && mapTemp?.entities && stores?.length > 0) {
      clearPins();
      const pins = generatePins();
      pins.forEach((pin) => {
        mapTemp.entities.push(pin);
      });
      const mainStore = stores[0] || {};
      const { lat, lng } = mainStore?.coordinates || {};
      mapTemp.setView({
        center: new Microsoft.Maps.Location(lat, lng)
      });
    }
  };

  useEffect(() => {
    if (stores?.[0] !== undefined) {
      populatePinsOnMap({ mapTemp: bingMap });
    }
  }, [stores, infoBox]);

  const handleViewChangeEnd = ({ mapEvent, map }) => {
    const { isInAnimation = true } = mapEvent;
    if (isInAnimation) {
      onMapViewChange(map.getCenter());
    }
  };

  const generateMap = () => {
    const { lat, lng } = stores?.[0]?.coordinates || { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
    const {
      disablePanning,
      disableScrollWheelZoom,
      showDashboard,
      zoom
    } = mapOptionsMerged;

    const map = new Microsoft.Maps.Map(`#${storeMapId}`, {
      center: new Microsoft.Maps.Location(lat, lng),
      credentials: 'AoKTFnu3hdkRgVWl45fcxdz0WSDP6hroMDcPKXE4ZkQql3hPt3PtkCDwtNRCYgYA',
      disablePanning,
      disableScrollWheelZoom,
      navigationBarMode: Microsoft.Maps.NavigationBarMode.compact,
      showDashboard,
      showMapTypeSelector: false,
      zoom
    });

    Microsoft.Maps.Events.addThrottledHandler(map, 'viewchangeend', (mapEvent) => {
      handleViewChangeEnd({ mapEvent, map });
    });

    return map;
  };

  const handleClickInfoBox = ({ ev, infoBoxTemp }) => {
    const target = ev?.originalEvent?.target || {};
    const isCloseAction = target.getAttribute('data-id') === 'js-store-overlay-close-button';
    if (isCloseAction) infoBoxTemp.setOptions({ visible: false });
  };

  const generateInfoBox = ({ map }) => {
    const infoBoxTemp = new Microsoft.Maps.Infobox(map.getCenter(), {
      visible: false
    });
    infoBoxTemp.setMap(map);
    Microsoft.Maps.Events.addHandler(
      infoBoxTemp, 'click', (ev) => handleClickInfoBox({ ev, infoBoxTemp })
    );
    return infoBoxTemp;
  };

  const bingMapOnLoad = () => {
    setShowPlaceholder(false);
    const bingMapTemp = generateMap();
    const infoBoxTemp = generateInfoBox({ map: bingMapTemp });
    setBingMap(bingMapTemp);
    setInfoBox(infoBoxTemp);
  };

  useScript({
    async: true,
    defer: true,
    callback: () => bingMapOnLoad(),
    src: 'https://www.bing.com/api/maps/mapcontrol?callback=bingMapOnLoad'
  });

  const height = mapOptionsMerged?.height;

  return (
    <div className="sui-z-10" style={{ height }} data-testid="store-map" data-component="StoreMap">
      <div id={storeMapId} />
      <div id="storePinOverlay" />
      {showPlaceholder && (
        <div className="sui-animate-pulse">
          <div className="sui-w-auto sui-bg-strong sui-rounded-lg" style={{ height }} />
        </div>
      )}
    </div>
  );
};

StoreMap.propTypes = {
  mapOptions: shape({
    disableInfoBox: bool,
    disablePanning: bool,
    disableScrollWheelZoom: bool,
    height: string,
    showDashboard: bool,
    zoom: number,
  }),
  omitStoreNumber: string,
  onMapViewChange: func,
  onStoreClick: func,
  stores: arrayOf(storeShape),
  storeMapId: string
};

StoreMap.defaultProps = {
  mapOptions: {
    disableInfoBox: false,
    disablePanning: false,
    disableScrollWheelZoom: false,
    height: '800px',
    showDashboard: true,
    zoom: 10,
  },
  omitStoreNumber: '',
  onMapViewChange: () => {},
  onStoreClick: () => {},
  stores: [],
  storeMapId: 'bingMap'
};

StoreMap.displayName = 'StoreMap';

export { StoreMap };
